import React, { useState, useEffect, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 } from 'uuid';
import PropTypes from 'prop-types';
import {
  setDeviceDisplayMode,
  addToastNotification,
  doDeviceGroupUpdate,
  searchDevicesV2,
  updateStatus,
  addFilter,
  doEditSingleDeviceGroup,
  removeSelectedDevicesData
} from 'redux/actions';
import {
  reactTableSortOnClickInFuncComp,
  distillProductType,
  isObjectEmpty,
  toggleDisplayNameForDevices,
  toggleDisplayNameForBundles
} from 'common/utils/helpers';
import { isItBundle } from 'common/utils/deviceTwinUtils';
import { deviceDisplayTypes, DEVICES_PAGE_TAB, LOADING_CIRCLE, Status } from 'common/utils/constants';
import ScrollReactTable from 'components/shared/ScrollReactTable/ScrollReactTable';
import SvgAF from 'components/shared/SvgAF/SvgAF';
import SearchBar from 'components/shared/SearchBar/SearchBar';
import Checkbox from 'components/shared/Checkbox/Checkbox';
import { CustomLabel } from 'components/shared/CustomLabel/CustomLabel';
import { IconAndText } from 'components/shared/SvgAF/SvgAF';
import { IconLoader } from 'common/IconLoader';
import './ActionDevicesTable.css';
import ToastNotificationActionTypes from 'components/shared/ToastNotifications/constants';
import {
  selectActiveDeviceDisplayMode,
  selectActiveTabName,
  selectDeviceGroups,
  selectSelectedDeviceGroupName,
  selectActiveSelectedDeviceDictionary
} from 'redux/selectors';

const ActionDevicesTableIdCell = (props) => {
  const {
    isDeviceInEnv,
    deviceGroups,
    isChecked,
    sendSelected,
    targetDeviceId,
    targetDeviceDisplay,
    type,
    bundleMembers,
    bundleId,
    hasCheckBox,
    hasRemoveButtons,
    dispatch,
    localDeviceGroupName,
    setActionPending,
    fetchAndUpdateDeviceList
  } = props;

  const [isExpanded, setIsExpanded] = useState(false);

  const activeTab = useSelector(selectActiveTabName);
  const currentSelectedDeviceGroupName = useSelector(selectSelectedDeviceGroupName);
  // let hasOneDevice = deviceGroups && deviceGroups[localDeviceGroupName]?.devices?.length === 1;
  let hasOneItem = Object.keys(deviceGroups?.[localDeviceGroupName]?.devicesDictionary || {})?.length === 1;
  let bundleName = null;

  if (bundleMembers && bundleMembers.length) {
    bundleName = bundleMembers[0]?.tags?.bundle?.name;
  }

  const handleRemoveDeviceFromList = () => {
    setActionPending(true);
    let deviceGroup = deviceGroups[localDeviceGroupName];
    let { deviceGroupId, name, devices, userId } = deviceGroup;
    let newDeviceIds = [];
    let deletedDeviceId = [];

    if (bundleId && bundleId === targetDeviceId) {
      let memberIds = bundleMembers.map((member) => member.id);
      deletedDeviceId = memberIds;
      newDeviceIds = devices.filter((deviceId) => !memberIds.includes(deviceId));
    } else {
      newDeviceIds = devices.filter((deviceId) => deviceId !== targetDeviceId);
      deletedDeviceId.push(targetDeviceId);
    }

    let cb_success = () => {
      setActionPending(false);
      localDeviceGroupName === currentSelectedDeviceGroupName &&
        dispatch(addFilter(activeTab, { deviceId: newDeviceIds }));
      fetchAndUpdateDeviceList(deviceGroupId, newDeviceIds);
      dispatch(removeSelectedDevicesData(activeTab, targetDeviceId));
      dispatch(
        addToastNotification({
          id: v4(),
          title: bundleName ? bundleName : targetDeviceId,
          description: 'removed.',
          actionMessage: 'Undo',
          actionType: ToastNotificationActionTypes.UNDO_REMOVED_DEVICE,
          deletedDeviceId: deletedDeviceId,
          groupName: localDeviceGroupName,
          callback: (isActionPending) => {
            setActionPending(isActionPending);
            localDeviceGroupName === currentSelectedDeviceGroupName &&
              dispatch(addFilter(activeTab, { deviceId: devices }));
            fetchAndUpdateDeviceList(deviceGroupId, devices);
          }
        })
      );
    };

    let cb_failed = () => {
      setActionPending(false);
      dispatch(
        addToastNotification({
          id: v4(),
          title: bundleName ? bundleName : targetDeviceId,
          description: 'Failed to remove ',
          deletedDeviceId: targetDeviceId,
          groupName: localDeviceGroupName,
          reverse: true
        })
      );
    };
    dispatch(
      doDeviceGroupUpdate(deviceGroupId, name, deviceGroup.default, newDeviceIds, userId, cb_success, cb_failed)
    );

    // if (currentSelectedDeviceGroupName === localDeviceGroupName) {
    //   let newDeviceIdList = deviceIdList && deviceIdList.filter((deviceId) => deviceId !== targetDeviceId);
    //   dispatch(updateLocalFilteredDevices(newDeviceIdList));
    // }
  };

  return (
    <div className="ActionDevicesTable_DeviceTable_IdCell">
      <div className="ActionDevicesTable_DeviceTable_IdCell_DefaultSection">
        {hasCheckBox && (
          <Checkbox
            isChecked={isChecked}
            onChange={(event) => {
              let selected = !isChecked;
              sendSelected(selected, targetDeviceId);
              event.stopPropagation(); //stop default row click stuff from happening
            }}
          />
        )}
        {isDeviceInEnv ? (
          targetDeviceDisplay
        ) : (
          <div
            className="ActionDevicesTable_DeviceTable_IdCell_NotInSystem"
            data-tip={'This device does not exist in the current environment/system.'}
          >
            {targetDeviceDisplay}
          </div>
        )}
        {(type === 'pivot' || bundleId) && (
          <SvgAF
            customClass={'ActionDevicesTable_DeviceTable_IdCell_CollapseIcon'}
            img={isExpanded ? IconLoader.icon_collapseunits : IconLoader.icon_expandunits}
            hover={isExpanded ? IconLoader.icon_collapseunits_hover : IconLoader.icon_expandunits_hover}
            onClick={() => {
              setIsExpanded(!isExpanded);
            }}
          />
        )}
        {hasRemoveButtons && (
          <button
            className="ActionDevicesTable_RemoveButton"
            disabled={hasOneItem}
            data-tip={hasOneItem ? 'It needs at least one on the list.' : 'Remove device from list.'}
            onClick={() => handleRemoveDeviceFromList()}
          >
            Remove
          </button>
        )}
      </div>
      {(type === 'pivot' || bundleId) && isExpanded && (
        <div className="ActionDevicesTable_DeviceTable_IdCell_DetailsSection">
          {bundleMembers.map((m, idx) => {
            let memberDisplayName = toggleDisplayNameForDevices(m, props.displayType);

            return (
              <CustomLabel
                key={idx}
                img={
                  distillProductType(m.properties?.reported?.productType) === 'donor'
                    ? IconLoader.icon_donor
                    : IconLoader.icon_service
                }
                text={memberDisplayName}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

const ActionDevicesTable = (props) => {
  const {
    localSelectedDevicesDictionary,
    setLocalSelectedDevicesDictionary,
    hasCheckBox,
    hasRemoveButtons,
    localDeviceGroupName
    // deviceGroups
  } = props;

  const [sorted, setSorted] = useState([]);
  const [renderKey, setRenderKey] = useState(0);
  const [actionPending, setActionPending] = useState(false);
  // const [clickedDeviceGroupId, setClickedDeviceGroupId] = useState(null);
  const [searchString, setSearchString] = useState('');
  const [deviceGroupDevicesObj, setDeviceGroupDevicesObj] = useState(null);

  const activeTab = useSelector(selectActiveTabName);
  const displayTextType = useSelector(selectActiveDeviceDisplayMode);
  const mixedDevicesDataDictionary = useSelector(selectActiveSelectedDeviceDictionary);
  const selectedDevicesData = { ...mixedDevicesDataDictionary };

  const deviceGroups = useSelector(selectDeviceGroups);
  const dispatch = useDispatch();

  let selectedBundlesLength = 0;
  if (!isObjectEmpty(localSelectedDevicesDictionary)) {
    selectedBundlesLength = Object.entries(localSelectedDevicesDictionary).filter(
      ([k, v]) => v?.checked && v?.matched && mixedDevicesDataDictionary?.[k]?.bundleId
    )?.length;
  }

  let selectedTotalDevicesLength = 0;

  if (!isObjectEmpty(localSelectedDevicesDictionary)) {
    mixedDevicesDataDictionary &&
      Object.entries(mixedDevicesDataDictionary).forEach(([k, v]) => {
        if (localSelectedDevicesDictionary[k]?.checked && localSelectedDevicesDictionary[k]?.matched) {
          if (mixedDevicesDataDictionary[k].bundleId) {
            selectedTotalDevicesLength += mixedDevicesDataDictionary[k].members.length;
          } else {
            selectedTotalDevicesLength += 1;
          }
        }
      });
  }

  const fetchAndUpdateDeviceList = (deviceGroupId, deviceGroupDevices) => {
    let searchBody = {
      size: 10000,
      filters: { deviceId: [...deviceGroupDevices] },
      coalesceBundles: true
    };

    dispatch(updateStatus(`searchDevicesV2-${deviceGroupId}`, Status.PENDING));
    setActionPending(true);

    let cb_success = (result) => {
      dispatch(updateStatus(`searchDevicesV2-${deviceGroupId}`, Status.SUCCESS));
      setDeviceGroupDevicesObj(result);
      if (result.bundles) {
        Object.keys(result.bundles).forEach((bundleId) => {
          mixedDevicesDataDictionary[bundleId] = result.bundles[bundleId];
        });
      }
      if (result.devices) {
        result.devices.forEach((deviceObj) => {
          mixedDevicesDataDictionary[deviceObj.deviceId] = deviceObj;
        });
      }

      let newLocalSelectedDevicesDictionary = {};
      Object.keys(mixedDevicesDataDictionary).forEach((deviceId) => {
        newLocalSelectedDevicesDictionary[deviceId] = { checked: true, matched: true };
      });

      dispatch(
        doEditSingleDeviceGroup(localDeviceGroupName, {
          ...deviceGroups[localDeviceGroupName],
          devices: [...deviceGroupDevices],
          devicesDictionary: mixedDevicesDataDictionary
        })
      );
      setLocalSelectedDevicesDictionary(newLocalSelectedDevicesDictionary);
      setActionPending(false);
    };

    let cb_failed = (error) => {
      dispatch(updateStatus(`searchDevicesV2-${deviceGroupId}`, Status.FAILURE));
      setActionPending(false);
      console.log('failed error', error);
    };

    dispatch(searchDevicesV2(searchBody, null, cb_success, cb_failed));
  };

  useEffect(() => {
    if (
      !deviceGroupDevicesObj &&
      // isObjectEmpty(mixedDevicesDataDictionary) &&
      deviceGroups &&
      deviceGroups[localDeviceGroupName] &&
      actionPending === false
    ) {
      let deviceGroupId = deviceGroups[localDeviceGroupName].deviceGroupId;
      let deviceGroupDevices = deviceGroups[localDeviceGroupName].devices;

      fetchAndUpdateDeviceList(deviceGroupId, deviceGroupDevices);
    }
    if (
      deviceGroups &&
      deviceGroups[localDeviceGroupName] &&
      Object.keys(localSelectedDevicesDictionary).length === 0 &&
      !props.hasCheckBox
    ) {
      let newLocalSelectedDevicesDictionary = {};
      deviceGroups[localDeviceGroupName].devices.forEach((deviceId) => {
        newLocalSelectedDevicesDictionary[deviceId] = { checked: true, matched: true };
      });
      setLocalSelectedDevicesDictionary(newLocalSelectedDevicesDictionary);
    } else if (Object.keys(localSelectedDevicesDictionary).length === 0) {
      let newLocalSelectedDevicesDictionary = {};
      if (deviceGroups && deviceGroups[localDeviceGroupName]) {
        deviceGroups[localDeviceGroupName].devices.forEach((deviceId) => {
          newLocalSelectedDevicesDictionary[deviceId] = localSelectedDevicesDictionary[deviceId];
        });
        setLocalSelectedDevicesDictionary(newLocalSelectedDevicesDictionary);
      }
    }
  }, [
    localSelectedDevicesDictionary,
    localDeviceGroupName,
    deviceGroups,
    renderKey,
    actionPending,
    searchString,
    deviceGroupDevicesObj,
    mixedDevicesDataDictionary
  ]);

  const deviceCell = (row) => {
    let targetDevice = mixedDevicesDataDictionary?.[row.value];
    let targetDeviceType = mixedDevicesDataDictionary?.[row.value]?.type.toUpperCase();
    let targetDeviceDisplay = row.value;
    let type = null;
    let isDeviceInEnv = true;
    let bundleObj = selectedDevicesData?.[row.original];

    let isBundle = isItBundle(bundleObj);
    if (deviceGroupDevicesObj) {
      isBundle = isItBundle(deviceGroupDevicesObj?.bundles[row.original]);
      bundleObj = deviceGroupDevicesObj?.bundles[row.original];
    }
    if (targetDeviceType != 'PIVOT 5G' && !isBundle) {
      type = 'echo';
      targetDeviceDisplay = toggleDisplayNameForDevices(targetDevice, displayTextType);
    } else if (targetDeviceType == 'PIVOT 5G' || isBundle) {
      type = 'pivot';
      targetDeviceDisplay = toggleDisplayNameForBundles(
        mixedDevicesDataDictionary?.[row.value] ? mixedDevicesDataDictionary[row.value] : bundleObj,
        displayTextType
      );
    } else {
      targetDeviceDisplay = row.value;
      isDeviceInEnv = false;
    }

    return (
      <ActionDevicesTableIdCell
        displayType={displayTextType}
        fetchAndUpdateDeviceList={fetchAndUpdateDeviceList}
        setActionPending={setActionPending}
        isDeviceInEnv={isDeviceInEnv}
        deviceGroups={deviceGroups}
        localDeviceGroupName={localDeviceGroupName}
        dispatch={dispatch}
        hasCheckBox={hasCheckBox}
        hasRemoveButtons={hasRemoveButtons}
        type={type}
        targetDeviceId={row.value}
        bundleMembers={mixedDevicesDataDictionary?.[row.value]?.members}
        bundleId={mixedDevicesDataDictionary?.[row.value]?.bundleId}
        isChecked={localSelectedDevicesDictionary?.[row.value]?.checked}
        targetDeviceDisplay={targetDeviceDisplay}
        sendSelected={(selected, deviceId) => {
          let newLocalSelectedDevicesDictionary = {
            ...localSelectedDevicesDictionary
          };

          selected
            ? (newLocalSelectedDevicesDictionary[deviceId].checked = true)
            : (newLocalSelectedDevicesDictionary[deviceId].checked = false);

          setLocalSelectedDevicesDictionary(newLocalSelectedDevicesDictionary);
        }}
      />
    );
  };

  const columns = [
    {
      id: 'id_col_id',
      minWidth: 220,
      Header: (row) => {
        let displayType = displayTextType;
        let deviceColHeader = 'Device ID';

        switch (displayType) {
          case deviceDisplayTypes.ID:
            deviceColHeader = activeTab === DEVICES_PAGE_TAB.pivot ? 'Pod Code' : 'Device ID';
            break;
          case deviceDisplayTypes.NAME:
            deviceColHeader = activeTab === DEVICES_PAGE_TAB.pivot ? 'Pod Name' : 'Device Name';
            break;
          default:
            break;
        }

        let isChecked =
          Object.keys(localSelectedDevicesDictionary) &&
          Object.keys(localSelectedDevicesDictionary).reduce((acc, curr, index) => {
            return acc && localSelectedDevicesDictionary[curr]?.checked;
          }, localSelectedDevicesDictionary[Object.keys(localSelectedDevicesDictionary)[0]]);

        return (
          <div className="ActionDevicesTable_DeviceTable_HeaderDiv ActionDevicesTable_DeviceTable_HeaderDiv_DeviceID">
            {hasCheckBox && (
              <Checkbox
                isChecked={isChecked}
                onChange={(event) => {
                  let newLocalSelectedDevicesDictionary = { ...localSelectedDevicesDictionary };
                  if (!isChecked) {
                    Object.keys(localSelectedDevicesDictionary).forEach((fd) => {
                      newLocalSelectedDevicesDictionary[fd].checked = true;
                    });
                  } else {
                    Object.keys(localSelectedDevicesDictionary).forEach((fd) => {
                      newLocalSelectedDevicesDictionary[fd].checked = false;
                    });
                  }
                  setLocalSelectedDevicesDictionary(newLocalSelectedDevicesDictionary);
                  event.stopPropagation(); //stop default row click stuff from happening
                }}
              />
            )}

            <div className="idNameSwitcher">
              <SvgAF
                img={displayType === deviceDisplayTypes.ID ? IconLoader.icon_toggle_id : IconLoader.icon_toggle_name}
                hover={
                  displayType === deviceDisplayTypes.ID
                    ? IconLoader.icon_toggle_id_hover
                    : IconLoader.icon_toggle_name_hover
                }
                data-tip={displayType === deviceDisplayTypes.ID ? 'Toggle to Name' : 'Toggle to ID'}
                onClick={() => {
                  setRenderKey(Math.random() * 10);
                  dispatch(
                    setDeviceDisplayMode(
                      displayType === deviceDisplayTypes.ID ? deviceDisplayTypes.NAME : deviceDisplayTypes.ID
                    )
                  );
                }}
              />
            </div>
            <IconAndText
              img={IconLoader.icon_table_sort}
              hoverImg={IconLoader.icon_table_sort_hover}
              text={deviceColHeader}
              reverse
              onClick={() => {
                reactTableSortOnClickInFuncComp(sorted, setSorted, 'id_col_id');
              }}
            />
          </div>
        );
      },
      className: 'ActionDevicesTable_DeviceTable_IdColumn',
      accessor: (id) => {
        return id;
      },
      Cell: deviceCell,
      sortMethod: (a, b) => {
        let objA = mixedDevicesDataDictionary[a];
        let objB = mixedDevicesDataDictionary[b];
        let a1 = isItBundle(objA)
          ? toggleDisplayNameForBundles(objA, displayTextType)
          : toggleDisplayNameForDevices(objA, displayTextType);
        let b1 = isItBundle(objB)
          ? toggleDisplayNameForBundles(objB, displayTextType)
          : toggleDisplayNameForDevices(objB, displayTextType);

        return a1.toLowerCase() > b1.toLowerCase() ? 1 : -1;
      }
    }
  ];

  const handleSearchQuery = (searchString) => {
    setSearchString(searchString);
    let newLocalSelectedDevicesDictionary = { ...localSelectedDevicesDictionary };
    if (searchString) {
      Object.keys(localSelectedDevicesDictionary).forEach((deviceId) => {
        if (
          deviceId.toLowerCase().includes(searchString.toLowerCase()) ||
          mixedDevicesDataDictionary[deviceId]?.id?.includes(searchString) ||
          (mixedDevicesDataDictionary[deviceId]?.members != null &&
            mixedDevicesDataDictionary[deviceId].members.find((d) => {
              return (
                (d.id && d.id.includes(searchString)) || (d.tags?.userLabel && d.tags.userLabel.includes(searchString))
              );
            })) ||
          (mixedDevicesDataDictionary[deviceId]?.name &&
            mixedDevicesDataDictionary[deviceId].name.toLowerCase().includes(searchString.toLowerCase())) ||
          (mixedDevicesDataDictionary[deviceId]?.code &&
            mixedDevicesDataDictionary[deviceId].code.toLowerCase().includes(searchString.toLowerCase()))
        ) {
          newLocalSelectedDevicesDictionary[deviceId].matched = true;
        } else {
          newLocalSelectedDevicesDictionary[deviceId].matched = false;
        }
      });

      setLocalSelectedDevicesDictionary(newLocalSelectedDevicesDictionary);
    } else {
      Object.keys(newLocalSelectedDevicesDictionary).forEach((deviceId) => {
        newLocalSelectedDevicesDictionary[deviceId].matched = true;
      });
      setLocalSelectedDevicesDictionary(newLocalSelectedDevicesDictionary);
    }
  };

  let data = [];
  let pageSize = 0;
  localSelectedDevicesDictionary &&
    Object.keys(localSelectedDevicesDictionary) &&
    Object.keys(localSelectedDevicesDictionary).forEach((deviceId) => {
      let selectedDeviceObj = localSelectedDevicesDictionary[deviceId];
      if (selectedDeviceObj?.matched) {
        data.push(deviceId);
        pageSize += 1;
      }
    });
  let overlay = null;
  if (actionPending) {
    overlay = <div className="Global_LoadingCircle_Overlay">{LOADING_CIRCLE}</div>;
  }

  return (
    <div className="ActionDevicesTable_DeviceList_Div" style={{ width: props.width }}>
      <div className="ActionDevicesTable_DevicesSelected_Div">
        <span>{'Total number of devices: '}</span>
        <span className="ActionDevicesTable_DevicesSelected_Number">{selectedTotalDevicesLength + ' '}</span>
        <span>{'Number of bundles: '}</span>
        <span className="ActionDevicesTable_DevicesSelected_Number">{selectedBundlesLength + ' '}</span>
      </div>
      <div className="ActionDevicesTable_SearchBar_Div">
        <div className="ActionDevicesTable_SearchBar_InnerDiv">
          <SearchBar defaultText={'Search'} searchQuery={(searchString) => handleSearchQuery(searchString)} />
        </div>
      </div>
      <div className="ActionDevicesTable_DeviceList_InnerDiv">
        <div className="ActionDevicesTable_DeviceTable_Div">
          <ScrollReactTable
            infinite={true}
            showPagination={false}
            pageSize={pageSize}
            noDataText={''}
            className={'Global_Table -highlight ActionDevicesTable_DeviceTable'}
            resizable={false}
            sortable={false}
            data={data}
            overlay={overlay}
            fetching={actionPending}
            sorted={sorted}
            onSortedChange={(newSorted, column, shiftKey) => {
              setSorted(newSorted);
            }}
            columns={columns}
          />
        </div>
      </div>
    </div>
  );
};

ActionDevicesTable.defaultProps = {
  width: '444px',
  hasCheckBox: true,
  hasRemoveButtons: false,
  localSelectedDevicesDictionary: {}
};

ActionDevicesTable.propTypes = {
  width: PropTypes.string,
  hasCheckBox: PropTypes.bool,
  hasRemoveButtons: PropTypes.bool,
  localSelectedDevicesDictionary: PropTypes.object
};

export default memo(ActionDevicesTable);
